import React from 'react'
import Helmet from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import { getSrc} from 'gatsby-plugin-image'
import useSiteMetadata from '../../helpers/useSiteMetadata'
import useLogo from '../../helpers/useLogo'

const Seo = (
    {
        titleTemplate,
        title,
        description,
        excerpt,
        meta,
        keywords,
        author,
        date,
        timeToRead,
        children,
        thumbnail,
        thumbnailAlt,
        data,
        img,
        locale,
        type,
        location,
        organization,
        noindex
    }
) => {

    const site = useSiteMetadata()
    const {logo} = useLogo()
    const logoSrc = getSrc(logo)
  
    const social = (author && author.social) || site.social || []
    const twitter =
      social.find(s => s.name && s.name.toLowerCase() === 'twitter') || {}
  
    description = excerpt || description || site.description
  
    //Image
    const imageSrc = getSrc(thumbnail || data)
    const imageUrl = imageSrc
    const image =  imageUrl || img || 'https://res.cloudinary.com/baidibaba/image/upload/v1676228608/New%20Foundations/link-image_epysjp.png'
    const imageAlt =  thumbnailAlt || 'New Foundations Community Chaplaincy'

    const metaTags = [
      { itemprop: 'name', content: title || site.title },
      { itemprop: 'description', content: description },
      { name: 'description', content: description },

      // Robots
      { name: 'robots', content: noindex ? 'none' : 'index, follow' }  ,    
  
      // Open Graph
      { property: 'og:title', content: title || site.title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: type || 'website' },
      { property: 'og:site_name', content: site.name },
      { property: 'og:url', content: location.href},
      { property: 'og:image', content: image},
      { property: 'og:image:alt', content: imageAlt},

  
      // Twitter Card
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:site', content: site.name },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:url', content: location.href },
      { name: 'twitter:creator', content: twitter.url },
    ]

    // Keywords
    if (keywords && keywords.length > 0) {
      metaTags.push({ name: 'keywords', content: keywords.join(', ') })
    }

    // Date
    if (date) {
      metaTags.push({ name: 'article:published_time', content: date })
    }

    // Time to Read
    if (timeToRead) {
      metaTags.push({ name: 'twitter:label1', value: 'Reading time' })
      metaTags.push({
        name: 'twitter:data1',
        value: `${timeToRead} min read`
      })
    }

  if (meta) {
    metaTags.push(meta)
  }

      /**
   * Structured Data (JSON-LD)
   */

  const scripts = []

  // Article
  if (title && author) {
    const articleJsonLd = helmetJsonLdProp({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      image: imageUrl,
      datePublished: date,
      author: {
        '@type': 'Person',
        name: author.name,
        url: author.slug
      }
    })
    scripts.push(articleJsonLd)
  }

    // Portfolio
    if (title && type === 'portfolio') {
      const articleJsonLd = helmetJsonLdProp({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        image: imageUrl,
        author: {
          '@type': 'Organization',
          name: 'New Foundations Community Chaplaincy',
          url: 'Baidibaba'
        }
      })
      scripts.push(articleJsonLd)
    }


  // Breadcrumb
  if (location) {
    const path = location.pathname.split('/')
    path.pop()
    const breadcrumbJsonLd = helmetJsonLdProp({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        path.map((e, index, array)=>{
          const iName = e.charAt(0).toUpperCase() + e.slice(1)
          const itemName = iName.split('').length === 0 ? 'Baidibaba' : iName.split('-').map(e => e.charAt(0).toUpperCase() + e.slice(1)).join(' ')
          let x
          switch (index) {
            case 0:
              x= `${site.siteUrl}`
              break;
            case 1:
              x= `${site.siteUrl}/${e}`
              break;
            case 2:
              x= `${site.siteUrl}/${array[index-1]}/${e}`
              break;
            case 3:
              x= `${site.siteUrl}/${array[index-2]}/${array[index-1]}/${e}`
              break;

            default:
              break;
          }
          const listItem = {
            '@type': 'ListItem',
            position: index + 1,
            name: itemName,
            item: `${x}`
          } 
          return listItem
        })
      ]
    })
    scripts.push(breadcrumbJsonLd)
  }

    if(organization === true) {
      const organizationJsonLd = helmetJsonLdProp({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        "@id": `${site.siteUrl}/#organization` ,
         name: 'New Foundations Community Chaplaincy',
         url: site.siteUrl,
         logo: `${site.siteUrl}${logoSrc}`,
         "sameAs": []
      })
      scripts.push(organizationJsonLd)
    }



  const links = []

  if (location) {
    links.push({ rel: 'canonical', href: location.href })
  }

    return(
        <Helmet
          htmlAttributes={{
            lang: locale || 'en'
          }}
          title={title}
          titleTemplate={titleTemplate || `%s - ${site.title}`}
          meta={metaTags}
          script={scripts}
          link={links}
        >
          {children}
        </Helmet>
    )
}

export default Seo